import { useParams } from "react-router-dom";
import { DayStrip } from "@/components/DayStrip";
import { DeleteWeekButton } from "@/components/DeleteWeekButton";
import { WidgetRows } from "@/components/WidgetRows";
import { usePreviousWeek, useWeek } from "@/hooks/useWeeks";
import { previousIsoWeek } from "@/lib/days";
import { completeWeek, reopenWeek } from "@/lib/repos/weeks";
import { getWidgetModule, isWeekWidgetEntry } from "@/widgets/registry";

export function Week() {
  const params = useParams<{ weekId: string }>();
  const weekId = decodeURIComponent(params.weekId ?? "");
  const { week, loading } = useWeek(weekId);
  const prevIso = week
    ? previousIsoWeek(week.data.year, week.data.weekNumber)
    : null;
  const { week: previousWeek } = usePreviousWeek(
    prevIso?.year ?? null,
    prevIso?.weekNumber ?? null,
  );

  if (loading) return <p>Laster…</p>;
  if (!week) return <p data-testid="week-not-found">Uken finnes ikke.</p>;

  const weekEntries = week.data.widgets.filter(
    (w) => w.config.enabled && isWeekWidgetEntry(w),
  );
  const entryById = new Map(weekEntries.map((e) => [e.config.id, e]));

  return (
    <div data-screen-label={`Uke ${week.data.weekNumber} / oversikt`}>
      <p className="week-cycle-label">
        Uke {week.data.weekNumber} · {week.data.year}
      </p>
      <DayStrip weekId={weekId} week={week.data} selected="week" />
      <div className="page-toolbar">
        {week.data.state === "open" ? (
          <button
            type="button"
            className="btn-primary"
            onClick={() => completeWeek(weekId)}
            data-testid="complete-week-button"
          >
            Marker uken som fullført
          </button>
        ) : (
          <button
            type="button"
            className="btn ghost"
            onClick={() => reopenWeek(weekId)}
            data-testid="reopen-week-button"
          >
            Åpne uke igjen
          </button>
        )}
      </div>

      {weekEntries.length === 0 ? (
        <p data-testid="week-empty">Ingen aktive widgets.</p>
      ) : (
        <WidgetRows
          widgets={weekEntries.map((e) => e.config)}
          renderWidget={(config) => {
            const entry = entryById.get(config.id);
            if (!entry) return null;
            const module = getWidgetModule(config.kind);
            if (module?.scope !== "week") return null;
            return (
              <module.WeekView
                key={config.id}
                config={config}
                weekData={entry.week}
                allWidgets={week.data.widgets}
                previousWeek={previousWeek?.data ?? null}
                week={week.data}
              />
            );
          }}
        />
      )}

      <DeleteWeekButton weekId={weekId} />
    </div>
  );
}
